import { useCallback, useEffect, useState } from "react";
import { Ban, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, imageUrl } from "@/lib/api";
import { Avatar } from "@/components/common";

const TABS = ["open", "removed", "dismissed"];

const Preview = ({ r }) => {
  if (r.kind === "message") return <p className="rounded-lg bg-secondary p-3 text-sm whitespace-pre-line" data-testid={`report-preview-${r.id}`}>{r.preview.body}</p>;
  if (r.status === "removed") return <p className="text-sm text-muted-foreground">{r.kind === "video" ? "Video" : "Photo"} removed.</p>;
  return r.kind === "video"
    ? <video src={imageUrl(r.target)} controls preload="metadata" className="aspect-video w-full max-w-sm rounded-lg bg-black" data-testid={`report-preview-${r.id}`} />
    : <img src={imageUrl(r.target)} alt="Reported content" className="h-48 w-40 rounded-lg object-cover" data-testid={`report-preview-${r.id}`} />;
};

const ReportCard = ({ r, onDone }) => {
  const [busy, setBusy] = useState("");
  const act = async (action, suspend = false) => {
    if (suspend && !window.confirm(`Remove this and suspend ${r.owner?.name || "this member"}?`)) return;
    setBusy(action + suspend);
    try { await api.post(`/admin/reports/${r.id}/resolve`, { action, suspend }); toast.success(action === "remove" ? "Content removed" : "Report dismissed"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <li className="surface p-5" data-testid={`report-card-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="chip capitalize" data-active>{r.kind}</span>
        <span className="font-semibold text-destructive">{r.reason_label}</span>
        <span className="text-muted-foreground">· {new Date(r.created_at).toLocaleString()}</span>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-[auto_1fr]">
        <Preview r={r} />
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-2">Posted by {r.owner ? <><Avatar person={r.owner} size={24} /> <a href={`/members/${r.owner.id}`} className="underline">{r.owner.name}</a></> : "a former member"}</p>
          <p className="text-muted-foreground">Reported by {r.reporter?.name || "a former member"}</p>
          {r.note && <p className="rounded-lg bg-secondary p-3" data-testid={`report-note-${r.id}`}>“{r.note}”</p>}
          {r.action?.suspended && <p className="text-destructive">Member suspended</p>}
        </div>
      </div>
      {r.status === "open" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className="btn-primary h-10 px-4 text-sm" disabled={!!busy} onClick={() => act("remove")} data-testid={`report-remove-${r.id}`}><Trash2 className="h-4 w-4" /> Remove content</button>
          <button type="button" className="btn-outline h-10 border-destructive/60 px-4 text-sm text-destructive" disabled={!!busy} onClick={() => act("remove", true)} data-testid={`report-remove-suspend-${r.id}`}><Ban className="h-4 w-4" /> Remove and suspend</button>
          <button type="button" className="btn-outline h-10 px-4 text-sm" disabled={!!busy} onClick={() => act("dismiss")} data-testid={`report-dismiss-${r.id}`}><Check className="h-4 w-4" /> Dismiss</button>
        </div>
      )}
    </li>
  );
};

export const ReportsQueue = () => {
  const [tab, setTab] = useState("open");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/reports", { params: { status: tab } }).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiError(e))), [tab]);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="mt-10" data-testid="admin-reports">
      <h2 className="font-display text-3xl">Reports</h2>
      <p className="mt-1 text-sm text-muted-foreground">Content members have flagged. Removing hides it for everyone and emails the member who posted it.</p>
      <div className="mt-4 flex gap-2" role="tablist">
        {TABS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className="chip capitalize" data-active={tab === t} onClick={() => setTab(t)} data-testid={`reports-tab-${t}`}>
            {t} {data?.counts?.[t] != null && <span className="ml-2 text-muted-foreground">{data.counts[t]}</span>}
          </button>
        ))}
      </div>
      <ul className="mt-4 space-y-4" data-testid="reports-list">
        {data?.reports?.length === 0 && <li className="text-sm text-muted-foreground" data-testid="reports-empty">No {tab} reports.</li>}
        {data?.reports?.map((r) => <ReportCard key={r.id} r={r} onDone={load} />)}
      </ul>
    </section>
  );
};
